import React from 'react'
import {Link as ChakraLink} from '@chakra-ui/react'
import {Link as RouterLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useGetLinkData} from '../../../../../contexts/category-url-context'
import {resolveGDILink} from '../../utils/gdi-link-resolver'
import {GDILinkContext, useIsInsideGDILink} from '../../utils/gdi-link-context'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import GDICustomDrawer from '../gdiDrawerBlock'
import ProductsPopover from '../gdiCtaBlock/products-popover'

const GDILinkBlock = ({children, linkProps, ...props}) => {
    const getLinkData = useGetLinkData()
    const isInsideLink = useIsInsideGDILink()
    const linkData = resolveGDILink(props, getLinkData)
    /* Consumers may pass `linkProps` to forward styling/props to the inner anchor
     * (e.g. nav consumers that want their own typography/spacing/animations on the
     * rendered link). `_hover` defaults to `textDecoration: 'none'`; callers can
     * override by passing their own `_hover` in `linkProps`. */
    const extraLinkProps = linkProps || {}
    const hoverProp = {textDecoration: 'none', ...(extraLinkProps._hover || {})}
    const passthroughLinkProps = {...extraLinkProps, _hover: hoverProp}

    /* Popover Product IDs: open a desktop popover / mobile drawer listing the
     * referenced products. Comma-separated IDs in LinkReference. Reuses the same
     * ProductsPopover component that GDICtaBlock uses so behavior stays in sync. */
    if (!isInsideLink && props.LinkType === 'Popover Product IDs' && typeof props.LinkReference === 'string') {
        const ids = props.LinkReference.split(',').map((id) => id.trim()).filter(Boolean)
        if (ids.length > 0) {
            const triggerLink = (
                <ChakraLink
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    aria-label={props.AriaLabel}
                    {...passthroughLinkProps}
                >
                    {children}
                </ChakraLink>
            )
            return (
                <GDILinkContext.Provider value={true}>
                    <ProductsPopover productIds={ids} trigger={triggerLink} />
                </GDILinkContext.Provider>
            )
        }
    }

    /* Custom Drawer: open a WebDrawer populated with CMS-authored GDIDrawerBlock content. */
    if (!isInsideLink && props.LinkType === 'Custom Drawer') {
        const drawerBlock = getExpandedFromContentArea(props.CustomDrawer)
        if (drawerBlock) {
            return (
                <GDICustomDrawer
                    drawerBlock={drawerBlock}
                    renderTrigger={({onOpen}) => (
                        <GDILinkContext.Provider value={true}>
                            <ChakraLink
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault()
                                    onOpen()
                                }}
                                aria-label={props.AriaLabel}
                                {...passthroughLinkProps}
                            >
                                {children}
                            </ChakraLink>
                        </GDILinkContext.Provider>
                    )}
                />
            )
        }
    }

    /* System Drawer ID: dispatch a gdi:openDrawer event so the relevant
     * modal/drawer provider opens. Supported values: login, register, mini-cart, search. */
    if (!isInsideLink && props.LinkType === 'System Drawer ID' && props.LinkReference) {
        const handleDrawerClick = (e) => {
            e.preventDefault()
            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('gdi:openDrawer', {detail: {drawer: props.LinkReference}})
                )
            }
        }
        return (
            <GDILinkContext.Provider value={true}>
                <ChakraLink
                    href="#"
                    onClick={handleDrawerClick}
                    aria-label={props.AriaLabel}
                    {...passthroughLinkProps}
                >
                    {children}
                </ChakraLink>
            </GDILinkContext.Provider>
        )
    }

    /* If an ancestor already wraps this subtree in a link, skip our own anchor to
     * avoid nested `<a>` elements (invalid HTML, breaks navigation). The ancestor's
     * link wins per the GDI spec. */
    if (isInsideLink || !linkData) return <>{children}</>

    const {href, isExternal, ariaLabel, target, rel} = linkData

    if (isExternal) {
        return (
            <GDILinkContext.Provider value={true}>
                <ChakraLink
                    href={href}
                    aria-label={ariaLabel}
                    target={target}
                    rel={rel}
                    {...passthroughLinkProps}
                >
                    {children}
                </ChakraLink>
            </GDILinkContext.Provider>
        )
    }

    return (
        <GDILinkContext.Provider value={true}>
            <ChakraLink
                as={RouterLink}
                to={href}
                aria-label={ariaLabel}
                target={target}
                rel={rel}
                {...passthroughLinkProps}
            >
                {children}
            </ChakraLink>
        </GDILinkContext.Provider>
    )
}

GDILinkBlock.propTypes = {
    LinkType: PropTypes.string,
    LinkReference: PropTypes.string,
    AriaLabel: PropTypes.string,
    OpenInNewTab: PropTypes.bool,
    HideBlockOn: PropTypes.string,
    CustomDrawer: PropTypes.array,
    /**
     * Optional props forwarded to the inner ChakraLink (styling, sx, etc.). Useful
     * for consumers that want to drive the link's appearance from outside while
     * keeping the LinkType-aware behavior provided by this component.
     */
    linkProps: PropTypes.object,
    children: PropTypes.node
}

export default GDILinkBlock
